import type { WidgetV2Defaults, WidgetV2MountOptions } from './types'
import {
  normalizeAdvancedSearchVariant,
  normalizeDetailCheckoutVariant,
  normalizeDetailVariant,
  normalizeListVariant,
  normalizeSearchVariant,
} from './layout-variants'
import { bindDetailBooking } from './detail-booking'
import { getAdvancedSearchCss, getAdvancedSearchTemplate } from './layouts/advanced-search'
import checkoutModalCss from './layouts/detail/checkout-modal.css?inline'
import { getDetailCss, getDetailTemplate } from './layouts/detail'
import { getListCss, getListTemplate } from './layouts/list'
import { getSearchCss, getSearchTemplate } from './layouts/search'
import { enhanceNumberSteppers, getStepperCss } from './number-stepper'
import { enhanceDateRangePicker } from './date-range-picker'

type TemplateContext = Record<string, unknown>
type ExprNode = string | { call: string; args: ExprNode[] }
const FILTERS_CHANGED_EVENT = 'uzw:v2:filters-changed'
const FILTERS_RENDERED_EVENT = 'uzw:v2:filters-rendered'
const LIST_ENHANCEMENT_CLEANUP = new WeakMap<HTMLElement, () => void>()
type FilterDefinition = {
  code: string
  label: string
  category_name?: string | null
  /** API liefert z. B. `boolean` | `number` | `text` */
  value_type: string
  unit?: string | null
  min_number?: number | null
  max_number?: number | null
}

function resolveEl(el: string | HTMLElement): HTMLElement {
  if (typeof el !== 'string') return el
  const n = document.querySelector(el)
  if (!n || !(n instanceof HTMLElement)) {
    throw new Error(`UrlaubszeitWidgetV2: Element nicht gefunden: ${el}`)
  }
  return n
}

export function mount(opts: WidgetV2MountOptions): void {
  const host = resolveEl(opts.el)
  host.innerHTML = ''
  host.setAttribute('data-uzw-v2-mounted', '1')
  const widgetType = String(opts.widget ?? 'list').trim().toLowerCase()
  if (widgetType === 'search') {
    mountSearchWidget(host, opts)
    return
  }
  if (widgetType === 'advanced-search') {
    void mountAdvancedSearchWidget(host, opts)
    return
  }
  if (widgetType === 'detail') {
    void mountDetailWidget(host, opts)
    return
  }
  if (widgetType !== 'list') {
    host.innerHTML =
      '<div style="padding:12px;border:1px dashed #94a3b8;border-radius:8px;font:14px/1.4 system-ui">Widget V2: Unbekannter Typ. Aktuell verfuegbar: list, search, advanced-search, detail.</div>'
    return
  }
  void mountListWidget(host, opts)
}

async function mountAdvancedSearchWidget(
  host: HTMLElement,
  opts: WidgetV2MountOptions,
  options: { includeList?: boolean } = {},
): Promise<void> {
  const current = readListFiltersFromLocation()
  const initial = readSearchFormStateFromLocation()
  const variant = normalizeAdvancedSearchVariant(opts.layoutVariant)
  const html = renderTemplate(getAdvancedSearchTemplate(variant), {
    q: initial.q,
    stayFrom: initial.stayFrom,
    stayTo: initial.stayTo,
    adults: initial.adults,
    children: initial.children,
    resetUrl: escapeHtml(window.location.pathname),
  })
  host.innerHTML = `<style>${getSearchCss()}${getListCss()}${getAdvancedSearchCss()}${getStepperCss()}</style>${html}`
  enhanceNumberSteppers(host)
  const resultsForm = host.querySelector('[data-results-form]')
  if (resultsForm instanceof HTMLFormElement) enhanceDateRangePicker({ form: resultsForm })

  const dynHost = host.querySelector('[data-results-dyn]')
  if (dynHost instanceof HTMLElement) {
    try {
      const defs = await fetchFilterDefinitions(opts.apiBaseUrl, opts.token, current)
      renderDynamicFilters(dynHost, defs, current)
      enhanceNumberSteppers(dynHost)
    } catch {
      dynHost.innerHTML = '<p class="uzw-v2-meta">Filter konnten nicht geladen werden.</p>'
    }
  }

  const form = host.querySelector('[data-results-form]')
  const liveStatus = host.querySelector('[data-results-live-status]')
  const setLiveStatus = (text: string) => {
    if (liveStatus instanceof HTMLElement) liveStatus.textContent = text
  }
  const clearLiveStatus = () => setLiveStatus('')
  if (form instanceof HTMLFormElement) {
    const applyFilters = () => {
      const latest = readListFiltersFromLocation()
      const query = collectResultsQuery(form, latest)
      // Filter change should jump back to first page.
      query.delete('page')
      const target = new URL(window.location.pathname, window.location.origin)
      const qs = query.toString()
      target.search = qs
      window.history.replaceState({}, '', `${target.pathname}${target.search}`)
      setLiveStatus('Aktualisiere Ergebnisse ...')
      window.dispatchEvent(new CustomEvent(FILTERS_CHANGED_EVENT))
    }
    const debouncedApply = debounce(applyFilters, 300)
    form.addEventListener('submit', (event) => {
      event.preventDefault()
      applyFilters()
    })
    form.addEventListener('input', () => {
      debouncedApply()
    })
    form.addEventListener('change', () => {
      debouncedApply()
    })
  }
  window.addEventListener(FILTERS_RENDERED_EVENT, clearLiveStatus)

  const listHost = host.querySelector('[data-results-list]')
  if (options.includeList && listHost instanceof HTMLElement) {
    await mountListWidget(listHost, opts)
  } else if (!options.includeList && listHost instanceof HTMLElement) {
    listHost.remove()
  }
}

/** Gäste-Popdown (horizontal): Toggle, Klick außerhalb, Escape — wie widget_old. */
function bindSearchGuestPicker(host: HTMLElement): void {
  const picker = host.querySelector('[data-uzw-v2-guest-picker]')
  if (!(picker instanceof HTMLElement)) return
  const toggle = picker.querySelector('[data-uzw-v2-guest-toggle]')
  const panel = picker.querySelector('[data-uzw-v2-guest-panel]')
  const summary = picker.querySelector('[data-uzw-v2-guest-summary]')
  const adultInp = picker.querySelector('input[name="adult_count"]')
  const childInp = picker.querySelector('input[name="child_count"]')
  if (!(toggle instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return
  if (!(summary instanceof HTMLElement)) return

  const readAdults = (): number => {
    if (adultInp instanceof HTMLInputElement) {
      const n = Number.parseInt(String(adultInp.value ?? '').trim(), 10)
      return Number.isFinite(n) && n >= 1 ? n : 2
    }
    return 2
  }
  const readChildren = (): number => {
    if (childInp instanceof HTMLInputElement) {
      const n = Number.parseInt(String(childInp.value ?? '').trim(), 10)
      return Number.isFinite(n) && n >= 0 ? n : 0
    }
    return 0
  }

  const updateSummary = (): void => {
    const a = readAdults()
    const c = readChildren()
    const kindLabel = c === 1 ? 'Kind' : 'Kinder'
    summary.textContent = `${a} Erwachsene, ${c} ${kindLabel}`
  }

  const closePanel = (): void => {
    toggle.setAttribute('aria-expanded', 'false')
    panel.hidden = true
  }

  const openPanel = (): void => {
    toggle.setAttribute('aria-expanded', 'true')
    panel.hidden = false
    if (adultInp instanceof HTMLInputElement) adultInp.focus()
  }

  toggle.addEventListener('click', (e) => {
    e.stopPropagation()
    const expanded = toggle.getAttribute('aria-expanded') === 'true'
    if (expanded) closePanel()
    else openPanel()
  })

  const onDocClick = (ev: MouseEvent): void => {
    if (!(ev.target instanceof Node)) return
    if (!picker.contains(ev.target)) closePanel()
  }
  document.addEventListener('click', onDocClick)

  const onKeydown = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') closePanel()
  }
  document.addEventListener('keydown', onKeydown)

  adultInp?.addEventListener('input', updateSummary)
  adultInp?.addEventListener('change', updateSummary)
  childInp?.addEventListener('input', updateSummary)
  childInp?.addEventListener('change', updateSummary)
  updateSummary()
}

/** Nur Suchfelder (für Submit + URL-Sync), inkl. Gäste — immer aus dem DOM. */
function collectSearchOnlyParams(form: HTMLFormElement): URLSearchParams {
  const params = new URLSearchParams()
  const setFromForm = (name: 'q' | 'stay_from' | 'stay_to' | 'adult_count' | 'child_count'): void => {
    const el = form.querySelector(`[name="${name}"]`)
    if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLSelectElement) && !(el instanceof HTMLTextAreaElement))
      return
    const raw = String(el.value ?? '').trim()
    if (name === 'adult_count') {
      const n = Number.parseInt(raw, 10)
      params.set(name, Number.isFinite(n) && n >= 1 ? String(n) : '2')
      return
    }
    if (name === 'child_count') {
      const n = Number.parseInt(raw, 10)
      params.set(name, Number.isFinite(n) && n >= 0 ? String(n) : '0')
      return
    }
    if (raw) params.set(name, raw)
  }
  setFromForm('q')
  setFromForm('stay_from')
  setFromForm('stay_to')
  setFromForm('adult_count')
  setFromForm('child_count')
  return params
}

/** Übernimmt weitere Widget-Query-Keys aus der aktuellen Adresse (z. B. Filter), ohne Suchfelder zu überschreiben. */
function mergeCrossViewParamsFromLocation(target: URLSearchParams): void {
  if (typeof window === 'undefined') return
  const src = new URLSearchParams(window.location.search)
  for (const key of WIDGET_CROSS_VIEW_QUERY_KEYS) {
    if (key === 'page') continue
    if (target.has(key)) continue
    const v = src.get(key)?.trim()
    if (v) target.set(key, v)
  }
}

/** Hängt Suchparameter per replaceState an die aktuelle URL (debounced) — Liste auf derselben Seite reagiert via Event. */
function bindSearchWidgetUrlSync(form: HTMLFormElement, opts: Pick<WidgetV2MountOptions, 'searchIncludeTags' | 'tags'>): void {
  const push = (): void => {
    if (typeof window === 'undefined' || typeof history.replaceState !== 'function') return
    try {
      const u = new URL(window.location.href)
      const merged = new URLSearchParams(u.search)
      for (const k of ['q', 'stay_from', 'stay_to', 'adult_count', 'child_count', 'page', 'id', 'checkout', 'booking_id']) {
        merged.delete(k)
      }
      collectSearchOnlyParams(form).forEach((v, k) => merged.set(k, v))
      if (opts.searchIncludeTags && opts.tags && opts.tags.length > 0) merged.set('tags', opts.tags.join(','))
      const qs = merged.toString()
      const next = `${u.pathname}${qs ? `?${qs}` : ''}${u.hash}`
      history.replaceState(null, '', next)
      window.dispatchEvent(new CustomEvent(FILTERS_CHANGED_EVENT))
    } catch {
      /* ignore */
    }
  }
  const debounced = debounce(push, 320)
  form.addEventListener('input', debounced)
  form.addEventListener('change', debounced)
}

function mountSearchWidget(host: HTMLElement, opts: WidgetV2MountOptions): void {
  const variant = normalizeSearchVariant(opts.layoutVariant)
  const searchResultsPath = normalizeSearchPath(opts.searchResultsPath)
  const initialSearch = readSearchFormStateFromLocation()
  const html = renderTemplate(getSearchTemplate(variant), {
    q: initialSearch.q,
    stayFrom: initialSearch.stayFrom,
    stayTo: initialSearch.stayTo,
    adults: initialSearch.adults,
    children: initialSearch.children,
    tagsLabel: opts.tags && opts.tags.length > 0 ? opts.tags.join(', ') : '',
    searchResultsPath,
  })
  host.innerHTML = `<style>${getSearchCss()}${getStepperCss()}</style>${html}`
  enhanceNumberSteppers(host)
  bindSearchGuestPicker(host)
  const form = host.querySelector('[data-search-form]')
  if (!(form instanceof HTMLFormElement)) return
  enhanceDateRangePicker({ form })
  form.addEventListener('submit', (event) => {
    event.preventDefault()
    const params = collectSearchOnlyParams(form)
    mergeCrossViewParamsFromLocation(params)
    if (opts.searchIncludeTags && opts.tags && opts.tags.length > 0) params.set('tags', opts.tags.join(','))
    const target = new URL(searchResultsPath, window.location.origin)
    const qs = params.toString()
    if (qs) target.search = qs
    window.location.assign(target.toString())
  })
  bindSearchWidgetUrlSync(form, opts)
}

async function mountListWidget(host: HTMLElement, opts: WidgetV2MountOptions): Promise<void> {
  LIST_ENHANCEMENT_CLEANUP.get(host)?.()
  LIST_ENHANCEMENT_CLEANUP.delete(host)
  const rerender = () => {
    void mountListWidget(host, opts)
  }
  const alreadyBound = host.getAttribute('data-uzw-v2-list-live-bound') === '1'
  if (!alreadyBound) {
    window.addEventListener(FILTERS_CHANGED_EVENT, rerender)
    host.setAttribute('data-uzw-v2-list-live-bound', '1')
  }
  const urlFilters = readListFiltersFromLocation()
  const pageFromUrl = Number.parseInt(String(urlFilters.page ?? ''), 10)
  const page = Math.max(1, Number.isFinite(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : (opts.page ?? 1))
  const pageSize = Math.max(1, opts.pageSize ?? 6)
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  })
  for (const [key, value] of Object.entries(urlFilters)) {
    if (!value || key === 'page') continue
    query.set(key, value)
  }
  if (!query.get('tags') && opts.tags && opts.tags.length > 0) query.set('tags', opts.tags.join(','))
  host.innerHTML =
    '<div style="padding:12px;border:1px dashed #94a3b8;border-radius:8px;font:14px/1.4 system-ui">Widget V2: Lade Unterkuenfte...</div>'
  try {
    const res = await fetch(`${opts.apiBaseUrl.replace(/\/$/, '')}/api/v1/public/widget/properties?${query.toString()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${opts.token}`,
      },
    })
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
    const data = (await res.json()) as {
      sales_channel_name: string
      sales_channel_code: string
      items: Array<{
        id: string
        property_number: string
        name: string
        rental_object_name: string
        max_adults: number
        max_children: number
        primary_image_url?: string | null
        images?: Array<{
          alt_text?: string | null
          owner_type?: string
          variants?: Array<{ name: string; url: string; avif_url?: string | null; webp_url?: string | null }>
        }>
      }>
    }
    const pagination = readPaginationFromHeaders(res.headers, page, pageSize, data.items.length)
    const detailBase = opts.detailPath?.trim()
    const selectCardImage = (images?: Array<{
      alt_text?: string | null
      owner_type?: string
      variants?: Array<{ name: string; url: string; avif_url?: string | null; webp_url?: string | null }>
    }>) => {
      const hasCard = (img: {
        variants?: Array<{ name: string; url: string; avif_url?: string | null; webp_url?: string | null }>
      }): boolean => Array.isArray(img.variants) && img.variants.some((v) => v.name === 'card')
      const propertyFirst = images?.find((img) => img.owner_type === 'property' && hasCard(img))
      if (propertyFirst) return propertyFirst
      return images?.find((img) => hasCard(img))
    }
    const items = data.items.map((it) => {
      const cardImage = selectCardImage(it.images)
      return {
      name: escapeHtml(it.name),
      propertyNumber: escapeHtml(it.property_number),
      rentalObjectName: escapeHtml(it.rental_object_name),
      maxAdults: it.max_adults,
      maxChildren: it.max_children,
      imageUrl: cardImage?.variants?.find((v) => v.name === 'card')?.url || it.primary_image_url || '',
      imageUrlAvif: cardImage?.variants?.find((v) => v.name === 'card')?.avif_url || '',
      imageUrlWebp: cardImage?.variants?.find((v) => v.name === 'card')?.webp_url || '',
      imageAlt: it.images?.find((img) => img.alt_text && img.alt_text.trim())?.alt_text || it.name,
      detailUrl: detailBase ? escapeHtml(buildDetailPageHref(detailBase, it.id)) : '',
      }
    })
    const variant = normalizeListVariant(opts.layoutVariant)
    const pageInfoHtml = pagination.showPageInfo
      ? buildListPageInfoHtml(pagination.page, pagination.totalPages, pagination.hasReliableTotals)
      : ''
    const prevPageUrl = pagination.hasPrevPage ? buildListPageUrl(urlFilters, pagination.page - 1) : ''
    const nextPageUrl = pagination.hasNextPage ? buildListPageUrl(urlFilters, pagination.page + 1) : ''
    const paginationLinksHtml = buildPaginationLinksHtml(
      pagination.page,
      pagination.totalPages,
      urlFilters,
      pagination.hasReliableTotals,
      pagination.hasNextPage,
    )
    const html = renderTemplate(getListTemplate(variant), {
      channelName: escapeHtml(data.sales_channel_name),
      channelCode: escapeHtml(data.sales_channel_code),
      items,
      slideVisible: opts.listSlideVisible ?? 1.2,
      slideBreakpoints: opts.listSlideBreakpoints ?? '',
      emptyStateHtml: items.length === 0 ? '<p class="uzw-v2-meta">Keine Treffer.</p>' : '',
      pageInfoHtml,
      paginationLinksHtml,
      hasPrevPage: pagination.hasPrevPage,
      hasNextPage: pagination.hasNextPage,
      prevPageUrl,
      nextPageUrl,
    })
    host.innerHTML = `<style>${getListCss()}</style>${html}`
    if (variant === 'slidebox') {
      const cleanup = initSlideboxList(host)
      if (cleanup) LIST_ENHANCEMENT_CLEANUP.set(host, cleanup)
    }
    window.dispatchEvent(new CustomEvent(FILTERS_RENDERED_EVENT))
  } catch (err) {
    host.innerHTML = `<div style="padding:12px;border:1px solid #fecaca;background:#fef2f2;color:#991b1b;border-radius:8px;font:14px/1.4 system-ui">Widget V2 Fehler: ${escapeHtml(
      err instanceof Error ? err.message : String(err),
    )}</div>`
    window.dispatchEvent(new CustomEvent(FILTERS_RENDERED_EVENT))
  }
}

function initSlideboxList(host: HTMLElement): (() => void) | null {
  const root = host.querySelector('[data-uzw-v2-slidebox]') as HTMLElement | null
  const track = host.querySelector('[data-slide-track]') as HTMLElement | null
  if (!root || !track) return null
  const prevBtn = host.querySelector('[data-slide-prev]') as HTMLButtonElement | null
  const nextBtn = host.querySelector('[data-slide-next]') as HTMLButtonElement | null
  const defaultVisibleRaw = Number.parseFloat(String(root.getAttribute('data-slide-visible') ?? '').trim())
  const defaultVisible = Number.isFinite(defaultVisibleRaw) && defaultVisibleRaw > 0 ? defaultVisibleRaw : 1.2
  const breakpoints = parseSlideboxBreakpoints(root.getAttribute('data-slide-breakpoints'))

  const applyVisible = () => {
    let visible = defaultVisible
    const ww = window.innerWidth
    for (const bp of breakpoints) {
      if (ww >= bp.minWidth) visible = bp.visible
    }
    root.style.setProperty('--uzw-v2-slide-visible', String(visible))
  }

  const updateButtons = () => {
    if (!prevBtn && !nextBtn) return
    const max = Math.max(0, track.scrollWidth - track.clientWidth)
    const left = track.scrollLeft
    if (prevBtn) prevBtn.disabled = left <= 2
    if (nextBtn) nextBtn.disabled = left >= max - 2
  }

  const scrollByCard = (dir: -1 | 1) => {
    const first = track.querySelector('.uzw-v2-item') as HTMLElement | null
    const step = first ? first.getBoundingClientRect().width + 12 : Math.max(240, track.clientWidth * 0.85)
    track.scrollBy({ left: dir * step, behavior: 'smooth' })
  }

  const onResize = debounce(() => {
    applyVisible()
    updateButtons()
  }, 120)
  const onScroll = debounce(updateButtons, 40)
  const onPrev = () => scrollByCard(-1)
  const onNext = () => scrollByCard(1)

  applyVisible()
  updateButtons()
  window.addEventListener('resize', onResize)
  track.addEventListener('scroll', onScroll)
  prevBtn?.addEventListener('click', onPrev)
  nextBtn?.addEventListener('click', onNext)

  return () => {
    window.removeEventListener('resize', onResize)
    track.removeEventListener('scroll', onScroll)
    prevBtn?.removeEventListener('click', onPrev)
    nextBtn?.removeEventListener('click', onNext)
  }
}

function parseSlideboxBreakpoints(raw: string | null): Array<{ minWidth: number; visible: number }> {
  const src = String(raw ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
  const out: Array<{ minWidth: number; visible: number }> = []
  for (const part of src) {
    const [minRaw, visRaw] = part.split(':').map((x) => x.trim())
    const minWidth = Number.parseInt(minRaw ?? '', 10)
    const visible = Number.parseFloat(visRaw ?? '')
    if (!Number.isFinite(minWidth) || minWidth < 0 || !Number.isFinite(visible) || visible <= 0) continue
    out.push({ minWidth, visible })
  }
  out.sort((a, b) => a.minWidth - b.minWidth)
  return out
}

function renderTemplate(template: string, context: TemplateContext): string {
  const withLoops = template.replace(/{{#each\s+([a-zA-Z0-9_.]+)}}([\s\S]*?){{\/each}}/g, (_match, path: string, inner: string) => {
    const value = getByPath(context, path)
    if (!Array.isArray(value) || value.length === 0) return ''
    return value
      .map((item) => {
        const loopCtx: TemplateContext =
          item && typeof item === 'object' ? { ...context, ...(item as Record<string, unknown>), this: item } : { ...context, this: item }
        return replaceIfBlocks(inner, loopCtx)
      })
      .join('')
  })
  return replaceIfBlocks(withLoops, context)
}

function replaceIfBlocks(template: string, context: TemplateContext): string {
  const withIfs = template.replace(
    /{{#if\s+([^}]+)}}([\s\S]*?)(?:{{else}}([\s\S]*?))?{{\/if}}/g,
    (_match, condExpr: string, truthyBlock: string, falsyBlock = '') => {
      const isTruthy = evaluateCondition(condExpr.trim(), context)
      return isTruthy ? truthyBlock : falsyBlock
    },
  )
  return replaceVars(withIfs, context)
}

function replaceVars(template: string, context: TemplateContext): string {
  return template.replace(/{{\s*([^{}]+?)\s*}}/g, (_match, exprRaw: string) => {
    const expr = exprRaw.trim()
    if (!expr || expr.startsWith('#') || expr.startsWith('/') || expr === 'else') return ''
    const value = evaluateExpression(expr, context)
    if (value == null) return ''
    return String(value)
  })
}

function evaluateExpression(expr: string, context: TemplateContext): unknown {
  const ast = parseExpression(expr)
  return evaluateAst(ast, context)
}

function evaluateCondition(expr: string, context: TemplateContext): boolean {
  return Boolean(evaluateExpression(expr, context))
}

function tokenizeExpression(expr: string): string[] {
  const re = /"[^"]*"|'[^']*'|\(|\)|[^\s()]+/g
  const out: string[] = []
  let m: RegExpExecArray | null
  while ((m = re.exec(expr)) !== null) {
    out.push(m[0])
  }
  return out
}

function parseExpression(expr: string): ExprNode {
  const tokens = tokenizeExpression(expr)
  if (tokens.length === 0) return ''
  const hasParens = tokens.includes('(') || tokens.includes(')')
  if (!hasParens) {
    if (tokens.length === 1) return tokens[0]
    return { call: tokens[0], args: tokens.slice(1) }
  }
  if (tokens[0] === '(') {
    const [node] = parseNode(tokens, 0)
    return node
  }
  const helper = tokens[0]
  const [args] = parseArgs(tokens, 1)
  return { call: helper, args }
}

function parseNode(tokens: string[], startIdx: number): [ExprNode, number] {
  const token = tokens[startIdx]
  if (!token) return ['', startIdx]
  if (token !== '(') return [token, startIdx + 1]
  const op = tokens[startIdx + 1]
  if (!op || op === ')' || op === '(') return ['', startIdx + 1]
  const [args, iAfterArgs] = parseArgs(tokens, startIdx + 2, ')')
  let i = iAfterArgs
  if (tokens[i] === ')') i += 1
  return [{ call: op, args }, i]
}

function parseArgs(tokens: string[], startIdx: number, stopToken?: string): [ExprNode[], number] {
  const args: ExprNode[] = []
  let i = startIdx
  while (i < tokens.length && (stopToken ? tokens[i] !== stopToken : true)) {
    const [arg, nextIdx] = parseNode(tokens, i)
    args.push(arg)
    i = nextIdx
  }
  return [args, i]
}

function evaluateAst(node: ExprNode, context: TemplateContext): unknown {
  if (typeof node === 'string') return resolveToken(node, context)
  const helper = node.call
  if (helper === 'upper') return String(evaluateAst(node.args[0] ?? '', context) ?? '').toUpperCase()
  if (helper === 'lower') return String(evaluateAst(node.args[0] ?? '', context) ?? '').toLowerCase()
  if (helper === 'default') {
    const value = evaluateAst(node.args[0] ?? '', context)
    if (value == null || String(value).trim() === '') return evaluateAst(node.args[1] ?? '', context) ?? ''
    return value
  }
  if (helper === 'eq') return compareLoose(evaluateAst(node.args[0] ?? '', context), evaluateAst(node.args[1] ?? '', context)) === 0
  if (helper === 'ne') return compareLoose(evaluateAst(node.args[0] ?? '', context), evaluateAst(node.args[1] ?? '', context)) !== 0
  if (helper === 'gt') return compareLoose(evaluateAst(node.args[0] ?? '', context), evaluateAst(node.args[1] ?? '', context)) > 0
  if (helper === 'gte') return compareLoose(evaluateAst(node.args[0] ?? '', context), evaluateAst(node.args[1] ?? '', context)) >= 0
  if (helper === 'lt') return compareLoose(evaluateAst(node.args[0] ?? '', context), evaluateAst(node.args[1] ?? '', context)) < 0
  if (helper === 'lte') return compareLoose(evaluateAst(node.args[0] ?? '', context), evaluateAst(node.args[1] ?? '', context)) <= 0
  if (helper === 'and') return node.args.every((arg) => Boolean(evaluateAst(arg, context)))
  if (helper === 'or') return node.args.some((arg) => Boolean(evaluateAst(arg, context)))
  if (helper === 'not') return !Boolean(evaluateAst(node.args[0] ?? '', context))
  return getByPath(context, helper)
}

function resolveToken(token: string | undefined, context: TemplateContext): unknown {
  if (!token) return undefined
  if (/^-?\d+(\.\d+)?$/.test(token)) return Number(token)
  if (token === 'true') return true
  if (token === 'false') return false
  if (token === 'null') return null
  if (token === 'undefined') return undefined
  if (
    (token.startsWith('"') && token.endsWith('"') && token.length >= 2) ||
    (token.startsWith("'") && token.endsWith("'") && token.length >= 2)
  ) {
    return token.slice(1, -1)
  }
  return getByPath(context, token)
}

function compareLoose(a: unknown, b: unknown): number {
  const an = typeof a === 'number' ? a : Number(a)
  const bn = typeof b === 'number' ? b : Number(b)
  if (Number.isFinite(an) && Number.isFinite(bn)) {
    if (an < bn) return -1
    if (an > bn) return 1
    return 0
  }
  const as = String(a ?? '')
  const bs = String(b ?? '')
  if (as < bs) return -1
  if (as > bs) return 1
  return 0
}

function getByPath(context: TemplateContext, path: string): unknown {
  const keys = path.split('.').filter(Boolean)
  let cur: unknown = context
  for (const key of keys) {
    if (!cur || typeof cur !== 'object') return undefined
    cur = (cur as Record<string, unknown>)[key]
  }
  return cur
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function normalizeSearchPath(pathRaw?: string): string {
  const raw = String(pathRaw ?? '').trim()
  if (!raw) return 'unterkuenfte.html'
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/')) return raw
  return raw
}

/** Relativ zur aktuellen Seite (Demo-Root oder /testhomepage/); absolute URLs unveraendert. */
function normalizeWidgetPath(pathRaw: string | undefined, fallback: string): string {
  const raw = String(pathRaw ?? '').trim()
  if (!raw) return fallback
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/')) return raw
  return raw
}

/** Zurueck vom Detail zur Listen-/Filterseite. */
function normalizeDetailListPath(pathRaw?: string): string {
  return normalizeWidgetPath(pathRaw, 'unterkuenfte.html')
}

/**
 * Query-Parameter, die zwischen Liste, Suche, Advanced-Search und Detail
 * in der URL mitgefuehrt werden (analog zur API-Listenabfrage).
 */
const WIDGET_CROSS_VIEW_QUERY_KEYS = [
  'page',
  'q',
  'tags',
  'adult_count',
  'child_count',
  'stay_from',
  'stay_to',
  'feature_codes',
  'num_filters',
  'text_filters',
] as const

/**
 * Baut einen Link auf `pathRaw` (relativ zur Origin) und uebernimmt die
 * aktuellen Werte der {@link WIDGET_CROSS_VIEW_QUERY_KEYS} aus der Browser-URL,
 * optional mit Ueberschreibungen bzw. Ausnahmen (z. B. `id` beim Wechsel der Unterkunft).
 */
function buildWidgetCrossNavUrl(
  pathRaw: string,
  options?: { set?: Record<string, string | null | undefined>; omitFromSource?: ReadonlySet<string> },
): string {
  const raw = String(pathRaw ?? '').trim()
  const base =
    raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/') ? raw : `/${raw}`
  const omit = options?.omitFromSource ?? new Set<string>()
  const applySet = (u: URL) => {
    const set = options?.set ?? {}
    for (const [k, v] of Object.entries(set)) {
      if (v == null || v === '') u.searchParams.delete(k)
      else u.searchParams.set(k, v)
    }
  }

  if (typeof window === 'undefined') {
    try {
      const u = new URL(base, 'http://localhost')
      applySet(u)
      return `${u.pathname}${u.search}`
    } catch {
      const sep = base.includes('?') ? '&' : '?'
      return `${base}${sep}id=${encodeURIComponent(String(options?.set?.id ?? ''))}`
    }
  }

  try {
    const u = new URL(base, window.location.origin)
    const src = new URLSearchParams(window.location.search)
    for (const key of WIDGET_CROSS_VIEW_QUERY_KEYS) {
      if (omit.has(key)) continue
      const v = src.get(key)?.trim()
      if (v) u.searchParams.set(key, v)
    }
    applySet(u)
    return `${u.pathname}${u.search}`
  } catch {
    const pathOnly = base.split('?')[0]
    const sp = new URLSearchParams()
    try {
      const src = new URLSearchParams(window.location.search)
      for (const key of WIDGET_CROSS_VIEW_QUERY_KEYS) {
        if (omit.has(key)) continue
        const v = src.get(key)?.trim()
        if (v) sp.set(key, v)
      }
    } catch {
      /* ignore */
    }
    for (const [k, v] of Object.entries(options?.set ?? {})) {
      if (v == null || v === '') sp.delete(k)
      else sp.set(k, v)
    }
    const qs = sp.toString()
    return qs ? `${pathOnly}?${qs}` : pathOnly
  }
}

function buildDetailPageHref(detailPathRaw: string | undefined, propertyId: string): string {
  const path = normalizeWidgetPath(detailPathRaw, 'detail.html')
  return buildWidgetCrossNavUrl(path, {
    set: { id: propertyId },
    omitFromSource: new Set(['id', 'checkout', 'booking_id']),
  })
}

const DETAIL_ATTRS_PLACEHOLDER = '__UZW_V2_DETAIL_ATTRS__'
const DETAIL_GALLERY_PLACEHOLDER = '__UZW_V2_DETAIL_GALLERY__'
const DETAIL_CANCELLATION_PLACEHOLDER = '__UZW_V2_DETAIL_CANCELLATION__'

type WidgetDetailAttribute = {
  code: string
  label: string
  category_name?: string | null
  value_type: string
  unit?: string | null
  value: string
}

type WidgetCancellationTier = {
  min_days_before_arrival: number
  fee_flat_cents?: number | null
  fee_percent?: number | null
  sort_index: number
}

type WidgetDetailImage = {
  assignment_id?: string
  src: string
  alt_text?: string | null
  category_name?: string | null
  season?: string
  owner_type?: string
  is_primary?: boolean
  custom_fields?: Array<{ code: string; label: string; value_type: string; sort_order: number; value_text: string }>
  variants?: Array<{ name: string; url: string; avif_url?: string | null; webp_url?: string | null }>
}

function buildDetailAttributesHtml(attributes: WidgetDetailAttribute[]): string {
  if (!attributes.length) return '<p class="uzw-v2-meta">Keine Merkmale hinterlegt.</p>'
  const isCancellationAttr = (a: WidgetDetailAttribute): boolean => {
    const haystack = `${a.code} ${a.label} ${String(a.category_name ?? '')}`.toLowerCase()
    return /storno|stornierung|cancel|cancellation/.test(haystack)
  }
  const cancellationAttrs = attributes.filter(isCancellationAttr)
  const regularAttrs = attributes.filter((a) => !isCancellationAttr(a))

  const cancellationHtml = cancellationAttrs.length
    ? `<section class="uzw-v2-detail-cancellation"><p class="uzw-v2-detail-cancellation-title">Stornierungsbedingungen</p>${cancellationAttrs
        .map((a) => {
          const lines = String(a.value ?? '')
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean)
          const body =
            lines.length > 1
              ? `<ul class="uzw-v2-detail-cancellation-list">${lines
                  .map((line) => `<li>${escapeHtml(line)}</li>`)
                  .join('')}</ul>`
              : `<p class="uzw-v2-detail-cancellation-text">${escapeHtml(String(a.value ?? ''))}</p>`
          return `<article class="uzw-v2-detail-cancellation-card"><p class="uzw-v2-detail-cancellation-label">${escapeHtml(
            a.label,
          )}</p>${body}</article>`
        })
        .join('')}</section>`
    : ''

  if (!regularAttrs.length) return cancellationHtml || '<p class="uzw-v2-meta">Keine Merkmale hinterlegt.</p>'

  const order: string[] = []
  const groups = new Map<string, WidgetDetailAttribute[]>()
  for (const a of regularAttrs) {
    const key = String(a.category_name ?? '').trim() || '__uncategorized__'
    if (!groups.has(key)) {
      groups.set(key, [])
      order.push(key)
    }
    groups.get(key)!.push(a)
  }
  const chip = (a: WidgetDetailAttribute) => {
    const unit = a.unit ? ` ${escapeHtml(a.unit)}` : ''
    return `<span class="uzw-v2-detail-chip">${escapeHtml(a.label)}: ${escapeHtml(a.value)}${unit}</span>`
  }
  const onlyUncategorized = order.length === 1 && order[0] === '__uncategorized__'
  const groupsHtml = order
    .map((key) => {
      const rows = (groups.get(key) ?? []).map(chip).join('')
      if (onlyUncategorized) return `<div class="uzw-v2-detail-chip-row">${rows}</div>`
      const label = key === '__uncategorized__' ? 'Ohne Kategorie' : key
      return `<div class="uzw-v2-detail-attr-group"><p class="uzw-v2-detail-attr-group-title">${escapeHtml(
        label,
      )}</p><div class="uzw-v2-detail-chip-row">${rows}</div></div>`
    })
    .join('')
  return `${cancellationHtml}${groupsHtml}`
}

function buildDetailCancellationHtml(tiers: WidgetCancellationTier[], scope: string): string {
  if (!Array.isArray(tiers) || tiers.length === 0) return ''
  const ordered = [...tiers].sort(
    (a, b) => b.min_days_before_arrival - a.min_days_before_arrival || a.sort_index - b.sort_index,
  )
  const rows = ordered
    .map((tier) => {
      const parts: string[] = []
      if (typeof tier.fee_percent === 'number' && tier.fee_percent > 0) parts.push(`${tier.fee_percent}%`)
      if (typeof tier.fee_flat_cents === 'number' && tier.fee_flat_cents > 0) parts.push(euro(tier.fee_flat_cents))
      const feeText = parts.length ? parts.join(' (mind. ') + (parts.length > 1 ? ')' : '') : '0 €'
      return `<li><strong>ab ${tier.min_days_before_arrival} Tage vor Anreise:</strong> ${escapeHtml(feeText)}</li>`
    })
    .join('')
  return `<section class="uzw-v2-detail-cancellation"><p class="uzw-v2-detail-cancellation-title">Stornierungsbedingungen</p><article class="uzw-v2-detail-cancellation-card"><ul class="uzw-v2-detail-cancellation-list">${rows}</ul></article></section>`
}

function buildDetailGalleryHtml(images: WidgetDetailImage[]): string {
  if (!Array.isArray(images) || images.length === 0) return ''
  const categories = Array.from(
    new Set(images.map((image) => String(image.category_name ?? '').trim() || 'Weitere')),
  ).sort((a, b) => a.localeCompare(b))
  const sourceLabel = (ownerType?: string): string =>
    ownerType === 'rental_object' ? 'Objektbild' : ownerType === 'property' ? 'Unterkunftsbild' : 'Bild'
  const seasonLabel = (season?: string): string => {
    const normalized = String(season ?? '')
      .trim()
      .toLowerCase()
    if (normalized === 'summer') return 'Sommer'
    if (normalized === 'winter') return 'Winter'
    return ''
  }
  const slidesHtml = images
    .map((img) => {
      const cardVariant = img.variants?.find((v) => v.name === 'card')
      const src = cardVariant?.url || img.src || ''
      if (!src) return ''
      const srcAvif = cardVariant?.avif_url || ''
      const srcWebp = cardVariant?.webp_url || ''
      const alt = String(img.alt_text ?? '').trim() || 'Bild'
      const season = seasonLabel(img.season)
      const source = sourceLabel(img.owner_type)
      const category = String(img.category_name ?? '').trim() || 'Weitere'
      return `<div class="uzw-v2-detail-gallery-slide" data-filter-source="${escapeHtml(
        String(img.owner_type ?? ''),
      )}" data-filter-season="${escapeHtml(String(img.season ?? ''))}" data-filter-category="${escapeHtml(
        category,
      )}"><figure class="uzw-v2-detail-gallery-card"><picture>${
        srcAvif ? `<source srcset="${escapeHtml(srcAvif)}" type="image/avif" />` : ''
      }${srcWebp ? `<source srcset="${escapeHtml(srcWebp)}" type="image/webp" />` : ''}<img src="${escapeHtml(
        src,
      )}" alt="${escapeHtml(alt)}" loading="lazy" /></picture><figcaption class="uzw-v2-detail-gallery-meta"><span class="uzw-v2-detail-gallery-badge">${escapeHtml(
        source,
      )}</span><span class="uzw-v2-detail-gallery-badge">${escapeHtml(category)}</span>${
        season ? `<span class="uzw-v2-detail-gallery-badge">${escapeHtml(season)}</span>` : ''
      }${img.is_primary ? '<span class="uzw-v2-detail-gallery-badge">Primaer</span>' : ''}</figcaption></figure></div>`
    })
    .join('')
  if (!slidesHtml) return ''
  const categoryOptions = ['Alle', ...categories]
    .map((label) => `<option value="${escapeHtml(label === 'Alle' ? 'all' : label)}">${escapeHtml(label)}</option>`)
    .join('')
  return `<section class="uzw-v2-detail-gallery"><p class="uzw-v2-meta" style="font-weight:700;margin:0">Bilder</p>
    <div class="uzw-v2-detail-gallery-filters">
      <button type="button" class="uzw-v2-detail-gallery-filter is-active" data-gallery-filter="source" data-value="all">Alle</button>
      <button type="button" class="uzw-v2-detail-gallery-filter" data-gallery-filter="source" data-value="property">Unterkunft</button>
      <button type="button" class="uzw-v2-detail-gallery-filter" data-gallery-filter="source" data-value="rental_object">Objekt</button>
      <button type="button" class="uzw-v2-detail-gallery-filter is-active" data-gallery-filter="season" data-value="all">Alle Saisons</button>
      <button type="button" class="uzw-v2-detail-gallery-filter" data-gallery-filter="season" data-value="summer">Sommer</button>
      <button type="button" class="uzw-v2-detail-gallery-filter" data-gallery-filter="season" data-value="winter">Winter</button>
      <select class="uzw-v2-select uzw-v2-detail-gallery-category-select" data-gallery-filter="category">${categoryOptions}</select>
    </div>
    <div class="uzw-v2-detail-gallery-slider" data-current-index="0">
      <button type="button" class="uzw-v2-detail-gallery-nav prev" data-gallery-nav="prev" aria-label="Vorheriges Bild">‹</button>
      <div class="uzw-v2-detail-gallery-track-wrap"><div class="uzw-v2-detail-gallery-track">${slidesHtml}</div></div>
      <button type="button" class="uzw-v2-detail-gallery-nav next" data-gallery-nav="next" aria-label="Nächstes Bild">›</button>
    </div>
    <p class="uzw-v2-meta uzw-v2-detail-gallery-count" data-gallery-count></p>
  </section>`
}

function initDetailGallery(host: HTMLElement, apiCurrentSeason?: string): void {
  const root = host.querySelector<HTMLElement>('.uzw-v2-detail-gallery')
  if (!root) return
  const slider = root.querySelector<HTMLElement>('.uzw-v2-detail-gallery-slider')
  const track = root.querySelector<HTMLElement>('.uzw-v2-detail-gallery-track')
  const count = root.querySelector<HTMLElement>('[data-gallery-count]')
  if (!slider || !track || !count) return
  const slides = Array.from(track.querySelectorAll<HTMLElement>('.uzw-v2-detail-gallery-slide'))
  if (!slides.length) return
  const nowMonth = new Date().getMonth() + 1
  const fallbackSeason = nowMonth >= 5 && nowMonth <= 9 ? 'summer' : 'winter'
  const normalizedApiSeason = String(apiCurrentSeason ?? '')
    .trim()
    .toLowerCase()
  const apiSeason = normalizedApiSeason === 'summer' || normalizedApiSeason === 'winter' ? normalizedApiSeason : ''
  const firstPropertySlide = slides.find((slide) => slide.dataset.filterSource === 'property')
  const defaultSeason =
    apiSeason ||
    ((firstPropertySlide?.dataset.filterSeason === 'summer' || firstPropertySlide?.dataset.filterSeason === 'winter')
      ? String(firstPropertySlide?.dataset.filterSeason)
      : fallbackSeason)
  const filters = { source: 'property', season: defaultSeason, category: 'all' }
  let visible: HTMLElement[] = [...slides]
  let index = 0
  let touchStartX: number | null = null
  let touchStartY: number | null = null
  const syncActiveButtons = () => {
    root
      .querySelectorAll<HTMLElement>('.uzw-v2-detail-gallery-filter[data-gallery-filter="source"]')
      .forEach((btn) => btn.classList.toggle('is-active', btn.dataset.value === filters.source))
    root
      .querySelectorAll<HTMLElement>('.uzw-v2-detail-gallery-filter[data-gallery-filter="season"]')
      .forEach((btn) => btn.classList.toggle('is-active', btn.dataset.value === filters.season))
    const categorySelect = root.querySelector<HTMLSelectElement>('.uzw-v2-detail-gallery-category-select')
    if (categorySelect) categorySelect.value = filters.category
  }
  const update = () => {
    visible = slides.filter((slide) => {
      const sourceOk = filters.source === 'all' || slide.dataset.filterSource === filters.source
      const seasonOk = filters.season === 'all' || slide.dataset.filterSeason === filters.season
      const categoryOk = filters.category === 'all' || slide.dataset.filterCategory === filters.category
      const show = sourceOk && seasonOk && categoryOk
      slide.style.display = show ? '' : 'none'
      return show
    })
    if (!visible.length) {
      index = 0
      track.style.transform = 'translateX(0)'
      count.textContent = 'Keine Bilder für diesen Filter.'
      return
    }
    if (index >= visible.length) index = 0
    visible.forEach((slide) => slide.classList.remove('is-current'))
    visible[index]?.classList.add('is-current')
    track.style.transform = `translateX(-${index * 100}%)`
    count.textContent = `Bild ${index + 1} von ${visible.length}`
  }
  root.querySelectorAll<HTMLElement>('[data-gallery-filter]').forEach((el) => {
    if (el.tagName === 'SELECT') {
      el.addEventListener('change', () => {
        filters.category = (el as HTMLSelectElement).value || 'all'
        index = 0
        update()
      })
      return
    }
    el.addEventListener('click', () => {
      const kind = el.dataset.galleryFilter as 'source' | 'season'
      const value = el.dataset.value || 'all'
      filters[kind] = value
      root
        .querySelectorAll<HTMLElement>(`.uzw-v2-detail-gallery-filter[data-gallery-filter="${kind}"]`)
        .forEach((btn) => btn.classList.toggle('is-active', btn === el))
      index = 0
      update()
    })
  })
  root.querySelectorAll<HTMLElement>('[data-gallery-nav]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!visible.length) return
      const dir = btn.dataset.galleryNav === 'prev' ? -1 : 1
      index = (index + dir + visible.length) % visible.length
      update()
    })
  })
  const trackWrap = root.querySelector<HTMLElement>('.uzw-v2-detail-gallery-track-wrap')
  if (trackWrap) {
    trackWrap.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.touches[0]
        if (!touch) return
        touchStartX = touch.clientX
        touchStartY = touch.clientY
      },
      { passive: true },
    )
    trackWrap.addEventListener(
      'touchend',
      (event) => {
        const touch = event.changedTouches[0]
        if (!touch || touchStartX === null || touchStartY === null || !visible.length) return
        const dx = touch.clientX - touchStartX
        const dy = touch.clientY - touchStartY
        touchStartX = null
        touchStartY = null
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return
        if (dx < 0) {
          index = (index + 1) % visible.length
        } else {
          index = (index - 1 + visible.length) % visible.length
        }
        update()
      },
      { passive: true },
    )
  }
  syncActiveButtons()
  update()
}

function buildDetailMediaLinksHtml(images: WidgetDetailImage[]): string {
  if (!Array.isArray(images) || images.length === 0) return ''
  type LinkRow = { label: string; url: string; sortOrder: number }
  const grouped = new Map<string, LinkRow[]>()
  const order: string[] = []
  for (const image of images) {
    const category = String(image.category_name ?? '').trim() || 'Weitere'
    const fields = Array.isArray(image.custom_fields) ? image.custom_fields : []
    for (const field of fields) {
      const valueType = String(field.value_type ?? '').trim().toLowerCase()
      if (valueType !== 'url' && valueType !== 'file') continue
      const rawUrl = String(field.value_text ?? '').trim()
      if (!rawUrl) continue
      if (!grouped.has(category)) {
        grouped.set(category, [])
        order.push(category)
      }
      grouped.get(category)!.push({
        label: String(field.label ?? '').trim() || 'Link',
        url: rawUrl,
        sortOrder: Number.isFinite(Number(field.sort_order)) ? Number(field.sort_order) : 0,
      })
    }
  }
  if (order.length === 0) return ''
  return `<section class="uzw-v2-detail-media-links"><p class="uzw-v2-meta" style="font-weight:700;margin:0">Medien-Links</p>${order
    .map((category) => {
      const seen = new Set<string>()
      const rows = [...(grouped.get(category) ?? [])]
        .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label) || a.url.localeCompare(b.url))
        .filter((row) => {
        const key = `${row.label}::${row.url}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
        })
      if (!rows.length) return ''
      const linksHtml = rows
        .map(
          (row) =>
            `<a class="uzw-v2-detail-media-link" href="${escapeHtml(row.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(
              row.label,
            )}</a>`,
        )
        .join('')
      return `<div class="uzw-v2-detail-media-links-group"><p class="uzw-v2-detail-media-links-title">${escapeHtml(
        category,
      )}</p><div class="uzw-v2-detail-media-links-list">${linksHtml}</div></div>`
    })
    .join('')}</section>`
}

async function mountDetailWidget(host: HTMLElement, opts: WidgetV2MountOptions): Promise<void> {
  const variant = normalizeDetailVariant(opts.layoutVariant)
  const checkoutVariant = normalizeDetailCheckoutVariant(opts.detailLayoutVariantCheckout)
  const listPath = normalizeDetailListPath(opts.detailListPath)
  const backHref = escapeHtml(
    buildWidgetCrossNavUrl(listPath, { omitFromSource: new Set(['id', 'checkout', 'booking_id']) }),
  )
  let propertyId = String(opts.propertyId ?? '').trim()
  if (!propertyId && opts.propertyIdFromUrl && typeof window !== 'undefined') {
    propertyId = String(new URL(window.location.href).searchParams.get('id') ?? '').trim()
  }
  const checkoutCss = checkoutVariant === 'modal' ? checkoutModalCss : ''
  const styleBlock = `<style>${getListCss()}${getSearchCss()}${getStepperCss()}${getDetailCss(variant)}${checkoutCss}</style>`

  if (!propertyId) {
    host.innerHTML = `${styleBlock}<div class="uzw-v2-widget uzw-v2-detail uzw-v2-detail--error"><p class="uzw-v2-meta">Keine Unterkunft-ID. Bitte <code>?id=…</code> in der URL oder <code>data-property-id</code> setzen.</p><p class="uzw-v2-meta" style="margin-top:8px"><a class="uzw-v2-page-btn" href="${backHref}">Zur Uebersicht</a></p></div>`
    return
  }

  host.innerHTML = `${styleBlock}<p class="uzw-v2-meta">Lade Unterkunft …</p>`
  const base = opts.apiBaseUrl.replace(/\/$/, '')
  try {
    const res = await fetch(`${base}/api/v1/public/widget/properties/${encodeURIComponent(propertyId)}`, {
      method: 'GET',
      headers: { Accept: 'application/json', Authorization: `Bearer ${opts.token}` },
    })
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
    const data = (await res.json()) as {
      sales_channel_code: string
      sales_channel_name: string
      id: string
      property_number: string
      name: string
      rental_object_name: string
      uses_object_address: boolean
      max_adults: number
      max_children: number
      min_occupancy: number
      checkin_from_hour: number
      checkin_until_hour: number
      checkout_from_hour: number
      checkout_until_hour: number
      widget_tags: string[]
      current_season?: string
      primary_image_url?: string | null
      images?: WidgetDetailImage[]
      location: { city: string; postal_code: string; country: string } | null
      attributes: WidgetDetailAttribute[]
      cancellation_tiers?: WidgetCancellationTier[]
      cancellation_scope?: string
    }
    const loc = data.location
    const locationLine =
      loc && (loc.city || loc.postal_code || loc.country)
        ? escapeHtml([loc.postal_code, loc.city].filter(Boolean).join(' ')) +
          (loc.country ? ` · ${escapeHtml(loc.country)}` : '')
        : ''
    const tags =
      Array.isArray(data.widget_tags) && data.widget_tags.length > 0
        ? escapeHtml(data.widget_tags.join(', '))
        : ''
    const metaLine = `${escapeHtml(data.sales_channel_name)} (${escapeHtml(data.sales_channel_code)}) · ${escapeHtml(
      data.property_number,
    )}`
    const capacityLine = `Bis ${data.max_adults} Erw. / ${data.max_children} Kinder · mind. ${data.min_occupancy} Pers.`
    const checkinLine = `Check-in ${data.checkin_from_hour}–${data.checkin_until_hour} h · Check-out ${data.checkout_from_hour}–${data.checkout_until_hour} h`
    const cancellationInner = buildDetailCancellationHtml(data.cancellation_tiers ?? [], String(data.cancellation_scope ?? ''))
    const attrsInner = `<div class="uzw-v2-detail-attr-groups"><p class="uzw-v2-meta" style="font-weight:700;margin:0 0 6px">Merkmale</p>${buildDetailAttributesHtml(
      data.attributes,
    )}</div>`
    const galleryInner = `${buildDetailGalleryHtml(data.images ?? [])}${buildDetailMediaLinksHtml(data.images ?? [])}`
    const initialTravel = readSearchFormStateFromLocation()
    const detailFieldSuffix = propertyId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 12) || 'p'
    const shell = renderTemplate(getDetailTemplate(variant), {
      backUrl: backHref,
      title: escapeHtml(data.name),
      metaLine,
      capacityLine,
      locationLine,
      tagsLine: tags ? `Schlagworte: ${tags}` : '',
      checkinLine,
      detailFieldSuffix,
      detailAdults: initialTravel.adults,
      detailChildren: initialTravel.children,
      detailStayFrom: escapeHtml(initialTravel.stayFrom),
      detailStayTo: escapeHtml(initialTravel.stayTo),
      checkoutInModal: checkoutVariant === 'modal',
    })
    host.innerHTML = `${styleBlock}${shell
      .replace(DETAIL_GALLERY_PLACEHOLDER, galleryInner)
      .replace(DETAIL_ATTRS_PLACEHOLDER, attrsInner)
      .replace(DETAIL_CANCELLATION_PLACEHOLDER, cancellationInner)}`
    initDetailGallery(host, data.current_season)
    await bindDetailBooking(host, opts, propertyId, data.property_number)
  } catch (err) {
    host.innerHTML = `${styleBlock}<div class="uzw-v2-widget uzw-v2-detail uzw-v2-detail--error"><p class="uzw-v2-meta">${
      err instanceof Error ? escapeHtml(err.message) : escapeHtml(String(err))
    }</p><p class="uzw-v2-meta" style="margin-top:8px"><a class="uzw-v2-page-btn" href="${backHref}">Zur Uebersicht</a></p></div>`
  }
}

function readListFiltersFromLocation(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  const sp = new URLSearchParams(window.location.search)
  const out: Record<string, string> = {}
  for (const key of WIDGET_CROSS_VIEW_QUERY_KEYS) {
    const value = sp.get(key)?.trim()
    if (value) out[key] = value
  }
  return out
}

function readPaginationFromHeaders(
  headers: Headers,
  fallbackPage: number,
  fallbackPageSize: number,
  currentItemCount: number,
): {
  page: number
  totalPages: number
  hasPrevPage: boolean
  hasNextPage: boolean
  showPageInfo: boolean
  hasReliableTotals: boolean
} {
  const pageRaw = Number.parseInt(String(headers.get('x-page') ?? ''), 10)
  const pageSizeRaw = Number.parseInt(String(headers.get('x-page-size') ?? ''), 10)
  const totalPagesRaw = Number.parseInt(String(headers.get('x-total-pages') ?? ''), 10)
  const totalCountRaw = Number.parseInt(String(headers.get('x-total-count') ?? ''), 10)
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : fallbackPage
  const pageSize = Number.isFinite(pageSizeRaw) && pageSizeRaw > 0 ? pageSizeRaw : fallbackPageSize
  const hasTotalCount = Number.isFinite(totalCountRaw) && totalCountRaw >= 0
  const totalCount = hasTotalCount ? totalCountRaw : currentItemCount
  const computedTotalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1)
  const hasTotalPagesHeader = Number.isFinite(totalPagesRaw) && totalPagesRaw > 0
  const hasReliableTotals = hasTotalPagesHeader || hasTotalCount
  const totalPages = hasTotalPagesHeader ? totalPagesRaw : computedTotalPages
  const hasPrevPage = page > 1
  const hasNextPage = hasReliableTotals ? page < totalPages : currentItemCount >= pageSize
  const showPageInfo = hasReliableTotals || page > 1
  return { page, totalPages, hasPrevPage, hasNextPage, showPageInfo, hasReliableTotals }
}

function buildListPageInfoHtml(page: number, totalPages: number, hasReliableTotals: boolean): string {
  if (!hasReliableTotals) return `<p class="uzw-v2-meta">Seite ${page}</p>`
  return `<p class="uzw-v2-meta">Seite ${page} von ${totalPages}</p>`
}

function buildPaginationLinksHtml(
  page: number,
  totalPages: number,
  baseFilters: Record<string, string>,
  hasReliableTotals: boolean,
  hasNextPage: boolean,
): string {
  const numbers = hasReliableTotals ? getVisiblePageNumbers(page, totalPages) : getHeuristicPageNumbers(page, hasNextPage)
  if (numbers.length === 0) return ''
  const links: string[] = []
  let prev: number | null = null
  for (const n of numbers) {
    if (prev != null && n - prev > 1) {
      links.push('<span class="uzw-v2-page-ellipsis">…</span>')
    }
    if (n === page) {
      links.push(`<span class="uzw-v2-page-num uzw-v2-page-num--active" aria-current="page">${n}</span>`)
    } else {
      const href = buildListPageUrl(baseFilters, n)
      links.push(`<a class="uzw-v2-page-num" href="${href}">${n}</a>`)
    }
    prev = n
  }
  return `<div class="uzw-v2-page-numbers">${links.join('')}</div>`
}

function getVisiblePageNumbers(page: number, totalPages: number): number[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_x, idx) => idx + 1)
  const start = Math.max(1, page - 2)
  const end = Math.min(totalPages, page + 2)
  const nums = new Set<number>([1, totalPages])
  for (let n = start; n <= end; n += 1) nums.add(n)
  return Array.from(nums).sort((a, b) => a - b)
}

function getHeuristicPageNumbers(page: number, hasNextPage: boolean): number[] {
  if (hasNextPage) return [page, page + 1]
  return [page]
}

function buildListPageUrl(baseFilters: Record<string, string>, page: number): string {
  const target = new URL(window.location.href)
  const sp = new URLSearchParams(target.search)
  for (const [key, value] of Object.entries(baseFilters)) {
    if (!value || key === 'page') continue
    sp.set(key, value)
  }
  sp.set('page', String(Math.max(1, page)))
  target.search = sp.toString()
  return escapeHtml(`${target.pathname}${target.search}`)
}

async function fetchFilterDefinitions(
  apiBaseUrl: string,
  token: string,
  query?: Record<string, string>,
): Promise<FilterDefinition[]> {
  const base = apiBaseUrl.replace(/\/$/, '')
  const sp = new URLSearchParams(query ?? {})
  const qs = sp.toString()
  const res = await fetch(`${base}/api/v1/public/widget/filter-definitions${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
  return (await res.json()) as FilterDefinition[]
}

function renderDynamicFilters(container: HTMLElement, defs: FilterDefinition[], query: Record<string, string>): void {
  const groupsMode = String(container.getAttribute('data-groups-mode') ?? 'full').trim().toLowerCase()
  const accordion = groupsMode === 'accordion'
  const boolSet = new Set(
    String(query.feature_codes ?? '')
      .split(',')
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean),
  )
  const numMap = new Map<string, { min?: string; max?: string }>()
  for (const p of String(query.num_filters ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)) {
    const [code, min, max] = p.split(':')
    numMap.set(String(code ?? ''), { min, max })
  }
  const textMap = new Map<string, string>()
  for (const p of String(query.text_filters ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)) {
    const idx = p.indexOf(':')
    if (idx <= 0) continue
    textMap.set(p.slice(0, idx), p.slice(idx + 1))
  }

  const supportedVt = (d: FilterDefinition): d is FilterDefinition & { value_type: 'boolean' | 'number' | 'text' } => {
    const v = String(d.value_type ?? '')
      .trim()
      .toLowerCase()
    return v === 'boolean' || v === 'number' || v === 'text'
  }

  const filtered = defs.filter(supportedVt).map((d) => ({
    ...d,
    value_type: String(d.value_type).trim().toLowerCase() as 'boolean' | 'number' | 'text',
  }))

  if (filtered.length === 0) {
    container.innerHTML = ''
    return
  }

  const numBoundsAttrs = (d: FilterDefinition): { min: string; max: string } => {
    const min =
      d.min_number != null && Number.isFinite(Number(d.min_number)) ? ` min="${escapeHtml(String(d.min_number))}"` : ''
    const max =
      d.max_number != null && Number.isFinite(Number(d.max_number)) ? ` max="${escapeHtml(String(d.max_number))}"` : ''
    return { min, max }
  }

  const renderFilter = (d: FilterDefinition & { value_type: 'boolean' | 'number' | 'text' }): string => {
    if (d.value_type === 'boolean') {
      return `<label class="uzw-v2-chip-like"><input type="checkbox" name="df_bool_${escapeHtml(d.code)}" ${
        boolSet.has(d.code.toLowerCase()) ? 'checked' : ''
      }/>${escapeHtml(d.label)}</label>`
    }
    if (d.value_type === 'number') {
      const v = numMap.get(d.code) ?? {}
      const { min: bMin, max: bMax } = numBoundsAttrs(d)
      const unitSuffix = d.unit ? ` (${escapeHtml(d.unit)})` : ''
      return `<div class="uzw-v2-field"><label>${escapeHtml(d.label)}${unitSuffix}</label><div class="uzw-v2-dyn-row">
        <input name="df_num_min_${escapeHtml(d.code)}" type="number" placeholder="min" value="${escapeHtml(
          v.min ?? '',
        )}" data-uzw-v2-stepper${bMin}${bMax} />
        <input name="df_num_max_${escapeHtml(d.code)}" type="number" placeholder="max" value="${escapeHtml(
          v.max ?? '',
        )}" data-uzw-v2-stepper${bMin}${bMax} />
      </div></div>`
    }
    return `<div class="uzw-v2-field"><label>${escapeHtml(d.label)}</label><input name="df_text_${escapeHtml(
      d.code,
    )}" type="text" value="${escapeHtml(textMap.get(d.code) ?? '')}" placeholder="enthält..." /></div>`
  }

  /** Wie widget_old: Gruppen nach `category_name` aus der API, sonst „Weitere“. */
  const groups = new Map<string, (FilterDefinition & { value_type: 'boolean' | 'number' | 'text' })[]>()
  for (const d of filtered) {
    const category = (d.category_name || '').trim() || 'Weitere'
    const list = groups.get(category) ?? []
    list.push(d)
    groups.set(category, list)
  }

  const wrapGroup = (titleEscaped: string, bodyHtml: string): string => {
    if (!bodyHtml) return ''
    const items = `<div class="uzw-v2-dyn-group-items">${bodyHtml}</div>`
    if (!accordion) {
      return `<div class="uzw-v2-dyn-group"><p class="uzw-v2-dyn-group-title">${titleEscaped}</p>${items}</div>`
    }
    return `<details class="uzw-v2-dyn-group uzw-v2-dyn-group--accordion"><summary class="uzw-v2-dyn-group-title">${titleEscaped}</summary><div class="uzw-v2-dyn-accordion-body">${items}</div></details>`
  }

  const groupHtml = Array.from(groups.entries())
    .map(([category, items]) => wrapGroup(escapeHtml(category), items.map(renderFilter).join('')))
    .join('')

  container.innerHTML = `<div class="uzw-v2-dyn-wrap">${groupHtml}</div>`
}

function collectResultsQuery(form: HTMLFormElement, existing: Record<string, string>): URLSearchParams {
  const params = new URLSearchParams()
  const read = (name: string) => String((form.querySelector(`[name="${name}"]`) as HTMLInputElement | null)?.value ?? '').trim()
  for (const key of ['q', 'stay_from', 'stay_to', 'adult_count', 'child_count']) {
    const v = read(key)
    if (v) params.set(key, v)
  }
  if (existing.tags) params.set('tags', existing.tags)

  const hasBoolInputs = form.querySelector('input[name^="df_bool_"]') != null
  const boolCodes = Array.from(form.querySelectorAll('input[name^="df_bool_"]'))
    .filter((el) => el instanceof HTMLInputElement && el.checked)
    .map((el) => (el as HTMLInputElement).name.replace('df_bool_', '').trim())
    .filter(Boolean)
  if (boolCodes.length > 0) params.set('feature_codes', boolCodes.join(','))
  else if (!hasBoolInputs && existing.feature_codes) params.set('feature_codes', existing.feature_codes)

  const numParts: string[] = []
  Array.from(form.querySelectorAll('input[name^="df_num_min_"]')).forEach((el) => {
    if (!(el instanceof HTMLInputElement)) return
    const code = el.name.replace('df_num_min_', '').trim()
    const min = el.value.trim()
    const max = String((form.querySelector(`[name="df_num_max_${code}"]`) as HTMLInputElement | null)?.value ?? '').trim()
    if (!code || (!min && !max)) return
    numParts.push(`${code}:${min}:${max}`)
  })
  if (numParts.length > 0) params.set('num_filters', numParts.join(','))
  else if (!form.querySelector('input[name^="df_num_min_"]') && existing.num_filters) params.set('num_filters', existing.num_filters)

  const textParts: string[] = []
  Array.from(form.querySelectorAll('input[name^="df_text_"]')).forEach((el) => {
    if (!(el instanceof HTMLInputElement)) return
    const code = el.name.replace('df_text_', '').trim()
    const val = el.value.trim()
    if (!code || !val) return
    textParts.push(`${code}:${val}`)
  })
  if (textParts.length > 0) params.set('text_filters', textParts.join(','))
  else if (!form.querySelector('input[name^="df_text_"]') && existing.text_filters) params.set('text_filters', existing.text_filters)

  return params
}

function resolveWidgetApiBaseUrl(dataApiBaseAttr: string | null, defaults: WidgetV2Defaults): string {
  if (dataApiBaseAttr !== null) {
    return dataApiBaseAttr.trim()
  }
  const d = defaults.apiBaseUrl
  if (d === undefined || d === null) return ''
  return String(d)
}

function readSearchFormStateFromLocation(): {
  q: string
  stayFrom: string
  stayTo: string
  adults: number
  children: number
} {
  if (typeof window === 'undefined') {
    return { q: '', stayFrom: '', stayTo: '', adults: 2, children: 0 }
  }
  const sp = new URLSearchParams(window.location.search)
  const adultsRaw = Number.parseInt(String(sp.get('adult_count') ?? ''), 10)
  const childrenRaw = Number.parseInt(String(sp.get('child_count') ?? ''), 10)
  return {
    q: String(sp.get('q') ?? '').trim(),
    stayFrom: String(sp.get('stay_from') ?? '').trim(),
    stayTo: String(sp.get('stay_to') ?? '').trim(),
    adults: Number.isFinite(adultsRaw) && adultsRaw > 0 ? adultsRaw : 2,
    children: Number.isFinite(childrenRaw) && childrenRaw >= 0 ? childrenRaw : 0,
  }
}

export function mountDeclaredWidgets(root: ParentNode = document): number {
  let count = 0
  const defaults = window.UrlaubszeitWidgetV2Defaults ?? {}
  root.querySelectorAll('[data-widget-v2]').forEach((node) => {
    if (!(node instanceof HTMLElement)) return
    const apiBaseUrl = resolveWidgetApiBaseUrl(node.getAttribute('data-api-base'), defaults)
    const token = node.getAttribute('data-token')?.trim() || defaults.token
    if (!token) return
    const tags = String(node.getAttribute('data-tags') ?? '')
      .split(',')
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean)
    const pageSizeAttr = Number.parseInt(String(node.getAttribute('data-list-page-size') ?? ''), 10)
    const pageSize = Number.isFinite(pageSizeAttr) && pageSizeAttr > 0 ? pageSizeAttr : defaults.pageSize
    const listSlideVisibleAttr = Number.parseFloat(String(node.getAttribute('data-list-slide-visible') ?? '').trim())
    const listSlideVisible =
      Number.isFinite(listSlideVisibleAttr) && listSlideVisibleAttr > 0
        ? listSlideVisibleAttr
        : defaults.listSlideVisible
    const listSlideBreakpoints = node.getAttribute('data-list-slide-breakpoints')?.trim() || defaults.listSlideBreakpoints
    const layoutVariant = node.getAttribute('data-layout-variant')?.trim() || defaults.layoutVariant
    const searchResultsPath = node.getAttribute('data-search-results-path')?.trim() || defaults.searchResultsPath
    const searchIncludeTagsAttr = node.getAttribute('data-search-include-tags')
    const searchIncludeTags = searchIncludeTagsAttr == null ? Boolean(defaults.searchIncludeTags) : isTrueLike(searchIncludeTagsAttr)
    const detailPath = node.getAttribute('data-detail-path')?.trim() || defaults.detailPath
    const detailListPath = node.getAttribute('data-detail-list-path')?.trim() || defaults.detailListPath
    const detailLayoutVariantCheckout =
      node.getAttribute('data-layout-variant-checkout')?.trim() || defaults.detailLayoutVariantCheckout
    const propertyId = node.getAttribute('data-property-id')?.trim() || defaults.propertyId
    const propertyIdFromUrlAttr = node.getAttribute('data-property-id-from-url')
    const propertyIdFromUrl =
      propertyIdFromUrlAttr == null ? Boolean(defaults.propertyIdFromUrl) : isTrueLike(propertyIdFromUrlAttr)
    mount({
      el: node,
      apiBaseUrl,
      token,
      widget: node.getAttribute('data-widget-v2')?.trim() || undefined,
      layoutVariant,
      tags,
      pageSize,
      listSlideVisible,
      listSlideBreakpoints,
      searchResultsPath,
      searchIncludeTags,
      detailPath,
      detailListPath,
      detailLayoutVariantCheckout,
      propertyId,
      propertyIdFromUrl,
    })
    count += 1
  })
  return count
}

function shouldAutoMountDeclared(): boolean {
  if (typeof document === 'undefined') return false
  return Array.from(document.scripts).some(
    (s) =>
      s.hasAttribute('data-widget-v2-auto-mount') && /urlaubszeit-widget-v2\.js(\?|#|$)/i.test(s.src),
  )
}

function isTrueLike(value: string): boolean {
  const raw = String(value).trim().toLowerCase()
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on'
}

function debounce(fn: () => void, delayMs: number): () => void {
  let t: number | undefined
  return () => {
    if (t != null) window.clearTimeout(t)
    t = window.setTimeout(() => {
      fn()
    }, delayMs)
  }
}

declare global {
  interface Window {
    UrlaubszeitWidgetV2Defaults?: WidgetV2Defaults
    UrlaubszeitWidgetV2?: {
      mount: typeof mount
      mountDeclaredWidgets: typeof mountDeclaredWidgets
    }
  }
}

window.UrlaubszeitWidgetV2 = { mount, mountDeclaredWidgets }
if (shouldAutoMountDeclared()) {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountDeclaredWidgets(), { once: true })
  else mountDeclaredWidgets()
}
